import { yupResolver } from "@hookform/resolvers/yup";
import { GetDPSDetailResponse } from "@repo/api";
import {
    StepperIndexes,
    selectExistingFundsRows,
    selectNewFundsRows,
    setStepByName,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as Yup from "yup";
import { useScrollFirstError } from "../../hooks/useScrollFirstError";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { Downloads } from "../Downloads";
import { CheckboxInput } from "../Form/Checkbox";
import { ProgressBarList } from "../ProgressBarList";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import { TopRow } from "../TopRow";
import { TriggerArrowRight } from "../TriggersWithArrows";
import * as Styled from "./ChangesOverviewPage.styled";
import { EditInvestmentStrategyMode } from "./EditInvestmentStrategy/EditInvestmentStrategy.types";
import { PensionQuestionnaireStepProps } from "./PensionQuestionnaireStepper";
import { fillLabelsToFundRows, mapCurrentAllocationsFromDetail } from "./Questionnaire/QuestionnairePage.helpers";

type FormValues = {
    agreement: boolean;
};

export const ChangesOverviewPage: React.FC<PensionQuestionnaireStepProps & DefaultStepProps> = ({
    queries: { precontractualDocsQuery },
    setContinueCondition,
    queries: { questionnaireConfigQuery, usePensionDetail },
    contractId,
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTypedTranslation();
    const { data: config, refetch: refetchQuestionnaireConfig } = questionnaireConfigQuery ?? {};
    // fetch config if not available
    useEffect(() => {
        if (!config) refetchQuestionnaireConfig?.();
    }, [refetchQuestionnaireConfig, config]);

    const { data: pensionDetail, refetch: refetchPensionDetail } =
        usePensionDetail?.({ id: String(contractId), options: { enabled: Boolean(contractId) }, type: "UF" }) ?? {};

    // fetch pension detail if not available
    useEffect(() => {
        if (!pensionDetail) {
            refetchPensionDetail?.();
        }
    }, [pensionDetail, refetchPensionDetail]);

    // Mapping current situation fund which user preferred not editing
    const getCurrentFundAllocation = useCallback(
        (mode: EditInvestmentStrategyMode) => {
            const currentSituation = (pensionDetail as GetDPSDetailResponse | undefined)?.funds
                ? mapCurrentAllocationsFromDetail(pensionDetail as GetDPSDetailResponse, config, mode)
                : undefined;

            return currentSituation?.currentAllocations?.map((allocation) => ({
                id: allocation.fundId,
                label: config?.funds.find((f) => f.id === allocation.fundId)?.name ?? "",
                value: allocation.percentage,
            }));
        },
        [config?.funds, pensionDetail],
    );

    // memoize to avoid re-creating the schema on every render
    const schema = useMemo(
        () =>
            Yup.object({
                agreement: Yup.boolean()
                    .oneOf([true], t("investmentStrategy.changesOverview.overviewAcceptError"))
                    .required(),
            }),
        [t],
    );

    const methods = useForm<FormValues>({
        defaultValues: { agreement: false },
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const { trigger, formState, handleSubmit } = methods;
    const savedFundsRows = useAppSelector(selectExistingFundsRows); // Stavajici prispevky
    const newContributionRows = useAppSelector(selectNewFundsRows); // Nove prispevky

    const {
        data: precontractualDocs,
        refetch: refetchPrecontractualDocs,
        isLoading: isLoadingDocs,
    } = precontractualDocsQuery ?? {};
    // fetch precontractualDocs if not available
    useEffect(() => {
        if (!precontractualDocs) refetchPrecontractualDocs?.();
    }, [refetchPrecontractualDocs, precontractualDocs]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    useScrollFirstError<FormValues>(
        { formState },
        {
            behavior: "smooth",
            root: containerRef.current ?? undefined,
            topOffset: 100,
        },
        { auto: true, severities: ["error"] },
    );

    // Watch for changes in the agreement checkbox and show error if unchecked
    useEffect(() => {
        setContinueCondition?.(async () => {
            let isValid = false;

            await handleSubmit(
                () => {
                    isValid = true;
                },
                () => {
                    isValid = false;
                },
            )();

            return isValid;
        });
    }, [setContinueCondition, trigger, handleSubmit]);

    return (
        <div ref={containerRef}>
            <FormProvider {...methods}>
                <Styled.PageWrapper>
                    <Styled.PageTitle>{t("investmentStrategy.changesOverview.header")}</Styled.PageTitle>
                    <Styled.IntroParagraph>
                        {t("investmentStrategy.changesOverview.headerDescription")}
                    </Styled.IntroParagraph>
                    <Styled.ColumnsWrapper>
                        {/* Stavajici prispevky */}
                        <Styled.Column>
                            <Styled.SectionHeaderStack>
                                <Styled.SectionHeadingBold>
                                    {savedFundsRows
                                        ? t("investmentStrategy.changesOverview.titleExistingChanged")
                                        : t("investmentStrategy.changesOverview.titleExisting")}
                                </Styled.SectionHeadingBold>
                                {!savedFundsRows && (
                                    <Styled.SectionBodyRegular>
                                        {t("investmentStrategy.changesOverview.withoutChange")}
                                    </Styled.SectionBodyRegular>
                                )}
                            </Styled.SectionHeaderStack>
                            <ProgressBarList
                                rows={
                                    fillLabelsToFundRows(savedFundsRows, config) ?? getCurrentFundAllocation("existing")
                                }
                                wrapperComponent={TopRow}
                            />
                            <Styled.EditTriggerWrapper>
                                <TriggerArrowRight
                                    onClick={() => {
                                        savedFundsRows
                                            ? dispatch(setStepByName(StepperIndexes.editExistingInvestmentStrategyPage))
                                            : dispatch(setStepByName(StepperIndexes.whatToChange));
                                    }}
                                >
                                    {t("investmentStrategy.changesOverview.edit")}
                                </TriggerArrowRight>
                            </Styled.EditTriggerWrapper>
                        </Styled.Column>

                        {/* Nove prispevky */}
                        <Styled.Column>
                            <Styled.SectionHeaderStack>
                                <Styled.SectionHeadingBold>
                                    {newContributionRows
                                        ? t("investmentStrategy.changesOverview.titleNewChanged")
                                        : t("investmentStrategy.changesOverview.titleNew")}
                                </Styled.SectionHeadingBold>
                                {!newContributionRows && (
                                    <Styled.SectionBodyRegular>
                                        {t("investmentStrategy.changesOverview.withoutChange")}
                                    </Styled.SectionBodyRegular>
                                )}
                            </Styled.SectionHeaderStack>
                            <ProgressBarList
                                rows={
                                    fillLabelsToFundRows(newContributionRows, config) ?? getCurrentFundAllocation("new")
                                }
                                wrapperComponent={TopRow}
                            />
                            <Styled.EditTriggerWrapper>
                                <TriggerArrowRight
                                    onClick={() => {
                                        newContributionRows
                                            ? dispatch(setStepByName(StepperIndexes.editNewInvestmentStrategyPage))
                                            : dispatch(setStepByName(StepperIndexes.whatToChange));
                                    }}
                                >
                                    {t("investmentStrategy.changesOverview.edit")}
                                </TriggerArrowRight>
                            </Styled.EditTriggerWrapper>
                        </Styled.Column>
                    </Styled.ColumnsWrapper>
                    <Styled.DocumentsSection>
                        <Styled.DocumentsHeading>
                            {t("investmentStrategy.changesOverview.titleDocuments")}
                        </Styled.DocumentsHeading>
                        <Styled.DocumentsParagraph>
                            {t("investmentStrategy.changesOverview.descriptionDocuments")}
                        </Styled.DocumentsParagraph>
                        {isLoadingDocs || !precontractualDocs ? (
                            <Styled.DownloadsSkeleton />
                        ) : (
                            <Downloads
                                name={precontractualDocs.name}
                                fileSize={precontractualDocs.fileSize}
                                mimeType={precontractualDocs.mimeType}
                                downloadPath={precontractualDocs.downloadPath}
                                isExternalUrl={true}
                            />
                        )}
                    </Styled.DocumentsSection>
                    <Styled.AgreementPaper>
                        <Styled.AgreementParagraph>
                            {t("investmentStrategy.changesOverview.overviewAccept")}
                        </Styled.AgreementParagraph>
                        <CheckboxInput
                            name="agreement"
                            label={t("investmentStrategy.changesOverview.acceptChanges")}
                            isRequired
                        />
                    </Styled.AgreementPaper>
                </Styled.PageWrapper>
            </FormProvider>
        </div>
    );
};
